import { rem } from 'polished'
import { memo, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import Counter from 'LuxKit/Counter'
import noop from 'lib/function/noop'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ExperiencePriceDetails from '../ExperiencePriceDetails'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import ExperienceTicketInfoMessage from './ExperienceTicketInfoMessage'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { useIntl } from 'react-intl'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'

const TicketOption = styled.div`
  padding: ${rem(12)} 0;
  display: grid;
  grid-row-gap: ${rem(4)};
  grid-column-gap: ${rem(8)};
  grid-template-columns: 1fr min-content;
  grid-template-areas:
    "tile counter"
    "tile counter"
    "price counter";

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr min-content min-content;
    grid-template-areas:
      "tile price counter"
      "tile price counter";
  }

  &:last-child {
    padding-bottom: 0;
  }
`

const TitleSection = styled(VerticalSpacer)`
  grid-area: tile;
  align-self: center;
`

const PriceSection = styled(VerticalSpacer)`
  grid-area: price;
  align-self: center;
  text-align: justify;

  ${mediaQueryUp.tablet} {
    text-align: end;
  }
`

const CounterSection = styled.div`
  grid-area: counter;
  align-self: flex-start;

  ${mediaQueryUp.tablet} {
    align-self: center;
  }
`

const WarningText = styled(BodyText)`
  grid-column: 1 / -1;
`

function getLimitMessage(intl: ReturnType<typeof useIntl>, reason?: App.ExperiencePurchaseLimit['maxReason']) {
  if (reason === 'AVAILABILITY') {
    return intl.formatMessage({
      id: 'YKPEQozD45zLY6u20h16',
      defaultMessage: 'Available slots for this ticket has been reached',
      description: 'Warning message when all available slots for an experience ticket are taken',
    })
  }
  if (reason === 'LIMIT-PER-CUSTOMER') {
    return intl.formatMessage({
      id: 'nxNagfppseHTlmxL0Eee',
      defaultMessage: 'You have reached the maximum number of tickets you can purchase for this experience',
      description: 'Warning message when user hits per-customer ticket purchase limit',
    })
  }
  return intl.formatMessage({
    id: 'u27aZM5J4q2e7uDzDGDL',
    defaultMessage: 'Purchase limit for this experience has been reached',
    description: 'Warning message when the overall purchase limit for an experience ticket is reached',
  })
}

export type ExperienceTicketItemCountChangeHandler = (ticket: App.ExperienceBookingTicketView, nextCount: number) => void

interface Props {
  ticket: App.ExperienceBookingTicketView
  count?: number
  onTicketCountChange?: ExperienceTicketItemCountChangeHandler
  disabled?: boolean
  purchaseLimitRemaining?: number
  purchaseLimitReason?: App.ExperiencePurchaseLimit['maxReason']
  orderMode?: App.ExperienceOrderMode
  /**
   * When this ticket is set to complimenetary, it will assume the price is 'free'
   * and not show price details
   */
  isComplimentary?: boolean
  /**
   * HTML Input 'name', will influence what field this fills is on a form object
   * @default ticketCount
   */
  name?: string
  isLuxPlusEnabled?: boolean
  hasLuxPlusOrSubscriptionInCart?: boolean
  offer: App.ExperienceOffer
}

function ExperienceTicketItem(props: Props) {
  const {
    ticket,
    onTicketCountChange = noop,
    count = 0,
    disabled,
    purchaseLimitRemaining,
    purchaseLimitReason,
    orderMode = 'booking',
    isComplimentary,
    name = 'ticketCount',
    isLuxPlusEnabled = false,
    hasLuxPlusOrSubscriptionInCart = false,
    offer,
  } = props

  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const onCountChange = useCallback((nextCount: number) => {
    let actualNextCount
    if (nextCount < count) {
      // went down
      actualNextCount = nextCount < ticket.purchaseLimit.min ? 0 : nextCount
    } else {
      // went up
      actualNextCount = nextCount < ticket.purchaseLimit.min ? ticket.purchaseLimit.min : nextCount
    }
    onTicketCountChange?.(ticket, actualNextCount)
  }, [ticket, onTicketCountChange, count])

  const showDiscount = (ticket.discounts?.base.percentage ?? 0) >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW

  const { limit, message } = useMemo(() => {
    const res = { limit: 9999, message: '' }
    if (purchaseLimitRemaining != undefined && (purchaseLimitRemaining / ticket.participantsPerUnit) < 1) {
      res.limit = count
      res.message = getLimitMessage(intl, purchaseLimitReason)
    } else if (ticket.purchaseLimit) {
      res.limit = ticket.purchaseLimit.max
      res.message = getLimitMessage(intl, ticket.purchaseLimit.maxReason)
    }

    return res
  }, [intl, purchaseLimitReason, purchaseLimitRemaining, ticket, count])

  const showMessage = useMemo(() => {
    // if the count is greater than the limit, or the limit is 0, show the message
    // limit is 0 means the customer has reached the maximum purchase limit, this is calculated in the backend
    const shouldShow = (!!count && count >= limit) || (ticket.purchaseLimit?.max === 0)
    return shouldShow && !!message
  }, [count, limit, message, ticket])

  const bookByDate = dateFormatter(ticket.bookByDate, INTL_DMY_CASUAL_FORMAT)
  const limitBookingDate = dateFormatter(ticket.rateEndDate ?? ticket.bookByDate, INTL_DMY_CASUAL_FORMAT)

  const hasMemberPrice = !!ticket.memberPrice && isLuxPlusEnabled && hasLuxPlusOrSubscriptionInCart && orderMode !== 'gift-card'
  const pointsEarnCalculationRequest = useMemo(() => generateLoyaltyPointsCalculatorExperienceOptions(offer, {
    price: ticket.price * Math.max(count, 1),
    memberPrice: isLuxPlusEnabled ? (ticket.memberPrice ?? 0) * Math.max(count, 1) : 0,
  }), [count, isLuxPlusEnabled, offer, ticket.memberPrice, ticket.price])
  const ticketPrice = useMemo(() => {
    if (hasMemberPrice) {
      return ticket.memberPrice!
    }
    return ticket.price
  }, [hasMemberPrice, ticket])

  const minimumTicketsText = ticket.purchaseLimit?.min > 1 ? intl.formatMessage(
    {
      id: 'Hw3uBHGyRAN2iZUxRc-e',
      defaultMessage: 'Minimum of {count, plural, one {# ticket} other {# tickets}}',
      description: 'Info message showing minimum ticket purchase requirement for an experience',
    },
    { count: ticket.purchaseLimit.min },
  ) : undefined

  const addOnOnlyTooltip = intl.formatMessage({
    id: '8j1UnhbC-kx5zrOghVhL',
    defaultMessage: 'Can only be booked in addition to other tickets',
    description: 'Tooltip explaining that an add-on ticket must be combined with other tickets',
  })

  const requiredTicketTooltip = intl.formatMessage({
    id: 'D1buVwEw811SgE4WsIbm',
    defaultMessage: 'This ticket is required for this booking',
    description: 'Tooltip explaining that an experience ticket is required for the booking',
  })

  const bookableUntilTooltip = intl.formatMessage(
    {
      id: 'tQOVdKdtRntoBWmrVg27',
      defaultMessage: 'Please ensure you book by {bookByDate} for a date up to {limitBookingDate}.',
      description: 'Tooltip explaining the booking deadline and maximum event date for a ticket',
    },
    { bookByDate, limitBookingDate },
  )

  return (
    <TicketOption key={ticket.id}>
      <TitleSection gap={4}>
        <BodyText variant="medium">{ticket.name}</BodyText>
        {minimumTicketsText && <ExperienceTicketInfoMessage>{minimumTicketsText}</ExperienceTicketInfoMessage>}
        {ticket.isExtra && (
          <ExperienceTicketInfoMessage tooltip={addOnOnlyTooltip}>
            {intl.formatMessage({
              id: '-lWyksJGyqNasCTsJCaY',
              defaultMessage: 'Add-on only',
              description: 'Label indicating a ticket can only be purchased as an add-on to other tickets',
            })}
          </ExperienceTicketInfoMessage>
        )}
        {ticket.isRequired && (
          <ExperienceTicketInfoMessage tooltip={requiredTicketTooltip}>
            {intl.formatMessage({
              id: 'aXBU6HR9-qUq0tBuIKfq',
              defaultMessage: 'Required',
              description: 'Label indicating an experience ticket is required for an experience booking',
            })}
          </ExperienceTicketInfoMessage>
        )}
        {orderMode !== 'booking' && ticket.bookByDate && (
          <ExperienceTicketInfoMessage tooltip={bookableUntilTooltip}>
            {intl.formatMessage(
              {
                id: 'a1ISRg3JYh6WPNsvWIhs',
                defaultMessage: 'Bookable until {date}',
                description: 'Info message showing the last date a ticket can be booked',
              },
              { date: bookByDate },
            )}
          </ExperienceTicketInfoMessage>
        )}
        {showDiscount && <PriceRowValueDiscount
          type="exact"
          discountPercentage={ticket.discounts?.base.percentage ?? 0}
        />}
        {!isComplimentary && <LoyaltyPointsEarn
          size="S"
          calculationType="estimate"
          calculationRequests={pointsEarnCalculationRequest}
          offer={offer}
          hideStatusCredits
        />}
      </TitleSection>

      <PriceSection gap={4}>
        <Heading variant="heading6" colour={hasMemberPrice && !isComplimentary ? 'lux-plus' : 'normal-contrast'}>
          {isComplimentary && intl.formatMessage({
            id: 'Yua3ns3MFd2hw_00Y-iw',
            defaultMessage: 'Free',
            description: 'Price label for complimentary experience tickets in the experiences section',
          })}
          {!isComplimentary && <>
            <FormatCurrency value={ticketPrice} />
            <ExperiencePriceDetails ticket={ticket} ticketCount={count} />
          </>}
        </Heading>
        <ExperienceAppDiscountTag discountPercentage={ticket.discounts?.app.percentage} />
      </PriceSection>

      <CounterSection>
        <Counter
          name={`${name}.${ticket.id}`}
          disabled={disabled}
          value={count}
          onChange={onCountChange}
          max={limit}
          data-testid="experience-ticket-item-counter"
        />
      </CounterSection>
      {showMessage && <WarningText variant="small" colour="warning" align="end">
        {message}
      </WarningText>}
    </TicketOption>
  )
}

const mapStateToProps = (state: App.State) => {
  return {
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
    hasLuxPlusOrSubscriptionInCart: getHasLuxPlusOrSubscriptionInCart(state),
  }
}

export default connect(mapStateToProps)(memo(ExperienceTicketItem))
